import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Atmosphere } from "@/components/atmosphere/Atmosphere";
import { SiteFooter } from "@/components/SiteFooter";
import { PersonnelFile } from "@/components/secrets/PersonnelFile";
import { RequisitionForm } from "@/components/RequisitionForm";
import { RedactedText } from "@/components/redaction/RedactedText";
import { useSecrets } from "@/context/SecretsContext";
import { BOOK_SIX, PUZZLE } from "@/data/secrets";
import { SITE } from "@/data/series";
import { useSeo } from "@/hooks/use-seo";

const Denied = () => (
  <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
    <div className="panel max-w-md p-7">
      <span className="stamp">Clearance denied</span>
      <h1 className="mt-5 text-3xl font-semibold leading-tight">
        This file is not open to you.
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-[hsl(210_12%_78%)]">
        The sixth file opens for readers who can say what time it was when the
        record resumed. Three fragments are recoverable from the dossier. The
        transmit line is at the bottom of the page.
      </p>
      <p className="mt-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(215_10%_50%)]">
        {PUZZLE.hintOne}
      </p>
      <Link
        to="/"
        data-testid="clearance-denied-home"
        className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-primary px-5 font-mono text-[11px] uppercase tracking-[0.16em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to the dossier
      </Link>
    </div>
  </div>
);

export default function Clearance() {
  const { unlocked } = useSecrets();
  const navigate = useNavigate();

  useSeo({
    title: unlocked
      ? `The sixth file | ${SITE.seriesTitle}`
      : `Clearance denied | ${SITE.seriesTitle}`,
    description:
      "Restricted file. Not indexed, not listed, not open to the public.",
    path: "/clearance",
    noindex: true
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [unlocked]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (!unlocked) {
    return (
      <>
        <Atmosphere />
        <Denied />
      </>
    );
  }

  return (
    <>
      <Atmosphere />
      <div className="relative z-10 min-h-screen" data-testid="clearance-page">
        <header className="sticky top-0 z-40 border-b border-border bg-[hsl(220_18%_6%/0.88)] backdrop-blur-md">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
            <Link
              to="/"
              data-testid="clearance-close-button"
              className="inline-flex items-center gap-2 rounded-[10px] border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground transition-colors duration-150 hover:bg-white/5"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Close file
            </Link>
            <span className="stamp stamp-open">Clearance granted</span>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 pb-4 pt-12 sm:px-6 sm:pt-16">
          {/* ---------- the sixth file ---------- */}
          <section>
            <p className="eyebrow">File VI · partially declassified</p>
            <h1 className="mt-3 text-4xl font-semibold leading-[1.02] sm:text-5xl">
              The sixth file
            </h1>

            <div className="panel mt-8 overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-[hsl(220_18%_8%)] px-4 py-3 sm:px-5">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Book VI · title
                </span>
                <span className="stamp">{BOOK_SIX.status}</span>
              </div>

              <div className="px-4 py-6 sm:px-5">
                <p className="text-2xl font-semibold leading-tight sm:text-3xl">
                  <span className="redact px-1.5">{BOOK_SIX.titleMask}</span>
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(2_80%_66%)]">
                  Still withheld. Naming it gives away the answer.
                </p>

                <div className="mt-6 space-y-4 border-t border-border pt-6">
                  {BOOK_SIX.teaser.map((line, i) => (
                    <p
                      key={i}
                      className="text-[15px] leading-relaxed text-[hsl(210_14%_84%)] sm:text-base"
                    >
                      <RedactedText text={line} />
                    </p>
                  ))}
                </div>
              </div>

              <div className="border-t border-border bg-[hsl(220_18%_8%)] px-4 py-5 sm:px-5">
                <p className="eyebrow">Dedication · partially recovered</p>
                <p
                  className="mt-2 text-lg italic text-[hsl(32_70%_72%)]"
                  data-testid="book-six-dedication"
                >
                  <RedactedText text={BOOK_SIX.dedication} />
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.13em] text-[hsl(215_10%_52%)]">
                  {BOOK_SIX.dedicationNote}
                </p>
              </div>
            </div>
          </section>

          {/* ---------- personnel file ---------- */}
          <section className="mt-16">
            <div className="paper p-5 sm:p-7">
              <PersonnelFile variant="paper" />
            </div>
          </section>

          {/* ---------- requisition ---------- */}
          <section className="mt-16" id="requisition">
            <div className="flex items-center gap-3">
              <Lock className="h-4 w-4 text-[hsl(32_80%_66%)]" />
              <p className="eyebrow">Form AR-06 · advance requisition</p>
            </div>
            <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-[2.4rem]">
              Claim a copy before it exists.
            </h2>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[hsl(210_12%_80%)] sm:text-base">
              You found the missing six seconds, so you get the thing nobody
              else is offered: a direct line to the author to reserve an
              unreleased book. Tell him which file you want and how you want it
              inscribed. He answers these himself.
            </p>

            <div className="paper mt-8 p-5 sm:p-7">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[hsl(36_18%_74%)] pb-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(220_12%_38%)]">
                  Advance requisition · direct to author
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(174_62%_28%)]">
                  Channel open
                </p>
              </div>

              <div className="mt-6">
                <RequisitionForm />
              </div>
            </div>
          </section>

          <div className="mt-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-[10px] border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground transition-colors duration-150 hover:bg-white/5"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to the dossier
            </Link>
          </div>
        </main>

        <div className="mt-16">
          <SiteFooter />
        </div>
      </div>
    </>
  );
}
